import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { signInWithCustomToken } from "firebase/auth";
import { Loader2, ShieldCheck } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { OtpCodeInput } from "@/components/otp-code-input";
import { StatusBanner } from "@/components/status-banner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { DASHBOARD_REDIRECT } from "@/config/constants";
import { consumeMagicLink } from "@/features/auth/hooks/useAuthForm";
import { verifyTotp } from "@/lib/apiSdk";
import { auth } from "@/lib/firebase/client";

export const Route = createFileRoute("/auth/verify")({
  ssr: false,
  component: MagicLinkVerifyPage,
});

type TotpState = {
  pendingToken: string;
  code: string;
  isVerifying: boolean;
  error: string | null;
};

function MagicLinkVerifyPage() {
  const navigate = useNavigate();
  const { challenge_id, token, ctx, name, redirect, passkey_pending, invite } =
    Route.useSearch() as {
      challenge_id?: string;
      token?: string;
      ctx?: string;
      name?: string;
      redirect?: string;
      passkey_pending?: string;
      invite?: string;
    };

  const safeRedirect =
    redirect?.startsWith("/app/") ||
    redirect?.startsWith("/vault/") ||
    redirect?.startsWith("/trustee/")
      ? redirect
      : undefined;

  // The invite token arrives in the URL (which survives opening the email on another
  // device) and falls back to sessionStorage (same-device, where a mail client may have
  // stripped the param). `/join` is deliberately NOT in the redirect allowlist — the
  // destination is derived from this token instead, so there is only one validated path.
  const inviteToken =
    invite ??
    (typeof sessionStorage !== "undefined"
      ? (sessionStorage.getItem("pending_invite_token") ?? undefined)
      : undefined);

  const destination = inviteToken
    ? `/join/${inviteToken}`
    : (safeRedirect ?? DASHBOARD_REDIRECT);

  const [error, setError] = useState<string | null>(null);
  const [totpState, setTotpState] = useState<TotpState | null>(
    passkey_pending
      ? {
          pendingToken: passkey_pending,
          code: "",
          isVerifying: false,
          error: null,
        }
      : null
  );
  const hasVerified = useRef(false);

  useEffect(() => {
    if (hasVerified.current) return;
    hasVerified.current = true;

    // Passkey login already authenticated the user and only needs the TOTP step;
    // the pending token is supplied directly, so skip magic-link verification.
    if (passkey_pending) return;

    if (!challenge_id || !token || !ctx) {
      setError("Invalid or missing magic link parameters.");
      return;
    }

    consumeMagicLink(challenge_id, token, ctx, name, inviteToken)
      .then((result) => {
        if (result.kind === "success") {
          navigate({ to: destination, replace: true });
        } else {
          setTotpState({
            pendingToken: result.pending_token,
            code: "",
            isVerifying: false,
            error: null,
          });
        }
      })
      .catch((err: unknown) => {
        const status =
          typeof err === "object" && err && "status" in err
            ? Number((err as { status?: unknown }).status)
            : undefined;
        if (status === 401 || status === 403) {
          setError(
            "This link has expired or has already been used. Please request a new one."
          );
        } else if (status === 429) {
          setError(
            "Too many attempts. Please wait a moment and try requesting a new link."
          );
        } else {
          setError("Something went wrong. Please request a new sign-in link.");
        }
      });
  }, []);

  async function handleTotpVerify(code: string) {
    if (!totpState || code.length !== 6) return;
    setTotpState((s) => s && { ...s, isVerifying: true, error: null });
    try {
      const { custom_token } = await verifyTotp(totpState.pendingToken, code);
      await signInWithCustomToken(auth, custom_token);
      // Same destination logic as the non-TOTP branch — a 2FA-enabled invitee must land
      // on /join too, not the dashboard.
      navigate({ to: destination, replace: true });
    } catch (err: unknown) {
      const status =
        typeof err === "object" && err && "status" in err
          ? Number((err as { status?: unknown }).status)
          : undefined;
      const msg =
        status === 429
          ? "Too many attempts. Please wait and try again."
          : "Invalid code. Please try again.";
      setTotpState(
        (s) => s && { ...s, isVerifying: false, error: msg, code: "" }
      );
    }
  }

  if (totpState) {
    return (
      <div className="flex h-screen">
        <div className="bg-muted flex w-full flex-col items-center justify-center p-8">
          <Card className="w-full max-w-sm">
            <CardContent className="flex flex-col gap-6 p-8">
              <div className="flex flex-col items-center gap-2 text-center">
                <div className="bg-primary/10 flex size-12 items-center justify-center rounded-full">
                  <ShieldCheck className="text-primary size-6" />
                </div>
                <h1 className="text-2xl font-semibold">
                  Two-factor authentication
                </h1>
                <p className="text-muted-foreground text-sm">
                  Enter the 6-digit code from your authenticator app.
                </p>
              </div>
              <OtpCodeInput
                value={totpState.code}
                onChange={(v) => {
                  setTotpState((s) => s && { ...s, code: v, error: null });
                  if (v.length === 6) void handleTotpVerify(v);
                }}
                isPending={totpState.isVerifying}
                disabled={totpState.isVerifying}
              />
              {totpState.error && <StatusBanner error={totpState.error} />}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen">
        <div className="bg-muted flex w-full flex-col items-center justify-center p-8">
          <Card className="w-full max-w-sm">
            <CardContent className="flex flex-col gap-6 p-8">
              <div className="flex flex-col gap-2 text-center">
                <h1 className="text-2xl font-semibold">Link expired</h1>
              </div>
              <StatusBanner error={error} />
              <Button
                className="w-full"
                size="lg"
                onClick={() => navigate({ to: "/signin" })}
              >
                Request a new link
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="text-primary size-8 animate-spin" />
        <p className="text-muted-foreground text-sm">Signing you in…</p>
      </div>
    </div>
  );
}
